import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';
import './ShareResultModal.css';

// Карточка результата для мессенджеров и соцсетей.
//
// Бэкенд отдает SVG (см. ShareCardService), в PNG его переводит браузер: рисуем
// картинку в canvas и забираем blob. Внутри карточки нет ничего с чужих доменов,
// поэтому canvas остается «чистым» и PNG отдается без ошибок безопасности.
const CARD_WIDTH = 1200;
const CARD_HEIGHT = 630;
// Растр рисуем в двойном размере — карточка должна остаться четкой на телефоне
const SCALE = 2;

const ShareResultModal = ({ isOpen, onClose, eventId, routeId, rider }) => {
  const { t, language } = useLanguage();

  const [svg, setSvg] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  const riderId = rider ? rider.user_id : null;

  useEffect(() => {
    if (!isOpen || !eventId || !routeId || !riderId) return undefined;

    let cancelled = false;

    const fetchCard = async () => {
      setLoading(true);
      setError('');
      setCopied(false);

      try {
        const response = await axios.get(
          `/api/events/${eventId}/results/share/${routeId}/${riderId}`,
          { params: { language }, responseType: 'text' }
        );
        if (!cancelled) setSvg(response.data);
      } catch (err) {
        // Текст ошибки с бэкенда в интерфейс не пускаем: подписи здесь переводятся
        if (!cancelled) setError('load_error');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchCard();
    return () => { cancelled = true; };
  }, [isOpen, eventId, routeId, riderId, language]);

  // data: URL вместо blob: — так картинка гарантированно своя для canvas
  const source = useMemo(
    () => (svg ? `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}` : ''),
    [svg]
  );

  const publicLink = useMemo(
    () => (eventId ? `${window.location.origin}/events/${eventId}/results` : ''),
    [eventId]
  );

  const downloadPng = () => {
    if (!source) return;

    const image = new Image();

    image.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = CARD_WIDTH * SCALE;
      canvas.height = CARD_HEIGHT * SCALE;

      const context = canvas.getContext('2d');
      context.drawImage(image, 0, 0, canvas.width, canvas.height);

      canvas.toBlob((blob) => {
        if (!blob) {
          setError('render_error');
          return;
        }

        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `result-${eventId}-${routeId}-${riderId}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }, 'image/png');
    };

    image.onerror = () => setError('render_error');
    image.src = source;
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(publicLink);
      setCopied(true);
    } catch (err) {
      setError('copy_error');
    }
  };

  if (!isOpen) return null;

  const handleOverlayClick = (event) => {
    if (event.target === event.currentTarget) onClose();
  };

  return (
    <div className="share-result-overlay" onClick={handleOverlayClick}>
      <div className="share-result-modal">
        <div className="share-result-header">
          <div>
            <h2>{t('share.title')}</h2>
            {rider?.name && <div className="share-result-subtitle">{rider.name}</div>}
          </div>
          <button type="button" className="share-result-close" onClick={onClose}>×</button>
        </div>

        <div className="share-result-body">
          {loading ? (
            <div className="share-result-message">{t('common.loading')}</div>
          ) : error ? (
            <div className="share-result-error">{t(`share.${error}`)}</div>
          ) : (
            <img className="share-result-preview" src={source} alt={t('share.title')} />
          )}
        </div>

        <div className="share-result-actions">
          <button
            type="button"
            className="share-result-primary"
            onClick={downloadPng}
            disabled={!source || loading}
          >
            {t('share.download')}
          </button>
          <button type="button" className="share-result-secondary" onClick={copyLink}>
            {copied ? t('share.link_copied') : t('share.copy_link')}
          </button>
        </div>

        <div className="share-result-hint">{t('share.hint')}</div>
      </div>
    </div>
  );
};

export default ShareResultModal;
